import { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import { userSlice } from "../redux/user/userSlice";
import { getGamesData } from "../redux/asyncThunk/userActions";
import {
  CONTENT_TYPES,
  resolveGameContentType,
} from "../utils/contentType";

const { selectProject, showDialog, resetSelectedProject, closeDialog } =
  userSlice;

const EXPANDABLE_SECTIONS = [
  {
    type: CONTENT_TYPES.scenario,
    label: "Сценарии",
    icon: <AccountTreeOutlinedIcon fontSize="small" />,
    empty: "Пока нет сценариев",
  },
  {
    type: CONTENT_TYPES.trainer,
    label: "Тренажёр",
    icon: <QuizOutlinedIcon fontSize="small" />,
    empty: "Пока нет тренажёров",
  },
];

export default function SideMenu() {
  const dispatch = useDispatch();
  const location = useLocation();
  const games = useSelector((state) => state.user?.games);
  const selectedGameId = useSelector((state) => state.user?.selectedGameId);
  const user = useSelector((state) => state.user?.data?.user);
  const [expandedTypes, setExpandedTypes] = useState({});

  const isKnowledgeBaseActive = location.pathname.startsWith("/main/knowledge-base");
  const isRatingActive =
    location.pathname.startsWith("/main/rating") ||
    location.pathname.startsWith("/main/motivation");

  useEffect(() => {
    if (user?.id) {
      dispatch(getGamesData({ id: user.id }));
    }
  }, [dispatch, user?.id]);

  const groupedProjects = useMemo(() => {
    const groups = {
      [CONTENT_TYPES.knowledge_base]: [],
      [CONTENT_TYPES.scenario]: [],
      [CONTENT_TYPES.trainer]: [],
    };

    for (const game of games ?? []) {
      const type = resolveGameContentType(game);
      groups[type]?.push(game);
    }

    return groups;
  }, [games]);

  useEffect(() => {
    if (!selectedGameId || !games?.length) return;

    const activeGame = games.find((game) => game._id === selectedGameId);
    if (activeGame) {
      const type = resolveGameContentType(activeGame);
      if (type !== CONTENT_TYPES.knowledge_base) {
        setExpandedTypes((current) => ({ ...current, [type]: true }));
      }
    }
  }, [selectedGameId, games]);

  const handleActive = (id) => {
    dispatch(selectProject(id));
  };

  const handleSectionToggle = (type) => {
    setExpandedTypes((current) => ({
      ...current,
      [type]: !current[type],
    }));
  };

  return (
    <aside className="app-sidebar">
      <div className="app-sidebar__top">
        <Button
          variant="contained"
          size="small"
          className="app-btn app-btn--primary"
          onClick={() => dispatch(showDialog("new"))}
        >
          Создать проект
        </Button>

        <Link to="/main" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary"
            onClick={() => dispatch(resetSelectedProject())}
          >
            Рабочий стол
          </Button>
        </Link>

        <Link to="/main/price" style={{ textDecoration: "none" }}>
          <Button
            variant="outlined"
            size="small"
            className="app-btn app-btn--secondary"
            onClick={() => dispatch(resetSelectedProject())}
          >
            Тарифы
          </Button>
        </Link>

        <div className="app-sidebar__sections">
          <Link
            to="/main/knowledge-base"
            className={`app-sidebar__direct-link${
              isKnowledgeBaseActive ? " app-sidebar__direct-link--active" : ""
            }`}
            onClick={() => dispatch(closeDialog())}
          >
            <span className="app-sidebar__direct-link-icon">
              <MenuBookOutlinedIcon fontSize="small" />
            </span>
            <span className="app-sidebar__direct-link-label">База знаний</span>
          </Link>

          <Link
            to="/main/rating"
            className={`app-sidebar__direct-link${
              isRatingActive ? " app-sidebar__direct-link--active" : ""
            }`}
            onClick={() => {
              dispatch(closeDialog());
              dispatch(resetSelectedProject());
            }}
          >
            <span className="app-sidebar__direct-link-icon">
              <EmojiEventsOutlinedIcon fontSize="small" />
            </span>
            <span className="app-sidebar__direct-link-label">Рейтинг</span>
          </Link>

          {EXPANDABLE_SECTIONS.map(({ type, label, icon, empty }) => {
            const projects = groupedProjects[type] ?? [];
            const isExpanded = Boolean(expandedTypes[type]);

            return (
              <div
                key={type}
                className={`app-sidebar__section${
                  isExpanded ? " app-sidebar__section--expanded" : ""
                }`}
              >
                <button
                  type="button"
                  className="app-sidebar__section-toggle"
                  onClick={() => handleSectionToggle(type)}
                  aria-expanded={isExpanded}
                >
                  <span className="app-sidebar__section-toggle-main">
                    <span className="app-sidebar__section-icon">{icon}</span>
                    <span className="app-sidebar__section-label">{label}</span>
                    <span className="app-sidebar__section-count">
                      {projects.length}
                    </span>
                  </span>
                  <ExpandMoreIcon
                    className="app-sidebar__section-chevron"
                    fontSize="small"
                  />
                </button>

                {isExpanded ? (
                  <div className="app-sidebar__section-body">
                    {!projects.length ? (
                      <div className="app-sidebar__empty">{empty}</div>
                    ) : (
                      <ul className="app-sidebar__list">
                        {projects.map((project) => (
                          <li key={project._id}>
                            <Link
                              to={`/main/${project._id}`}
                              state={{ projectId: project._id, type: "edit" }}
                              className={`app-sidebar__link${
                                selectedGameId === project._id &&
                                !isKnowledgeBaseActive
                                  ? " app-sidebar__link--active"
                                  : ""
                              }`}
                              onClick={() => {
                                handleActive(project._id);
                                dispatch(closeDialog());
                              }}
                            >
                              {project.title}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>

        <div className="app-sidebar__divider" />
      </div>

      <div className="app-sidebar__footer">
        <Link
          to="/main/rules"
          onClick={() => dispatch(resetSelectedProject())}
          style={{ textDecoration: "none" }}
        >
          <Button variant="contained" size="small" className="app-btn app-btn--ghost">
            Правила
          </Button>
        </Link>
      </div>
    </aside>
  );
}
